import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FlightHoldOut } from '@appTypes/api.types';
import { TranslatePipe } from '@ngx-translate/core';
import { OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { PriceFormatPipe } from '@shared/pipes/price-format.pipe';
import { AviaTraceIdComponent } from '../avia-trace-id/avia-trace-id.component';

export interface AviaHoldRefusedNoticeData {
  hold: FlightHoldOut;
  /** the registration's RF number when the hold answer does not carry its own */
  rfNumber?: string | null;
  activityId?: string | null;
}

/**
 * "Saved without a ticket" (flight fare picker, QA doc features/flight-fare-picker/qa.md R-FP13 + processes/flight-booking-errors.md R-FH19 / R-FH20, B1 = option 1 - Amiran
 * 07.10.2026 "Keep it, operator re-holds"): the reservation IS registered, but the airline refused the hold (its price changed in
 * the seconds after the confirm check). Informative only: the RF number, the airline's old / new price, the code and the Trace ID;
 * an operator re-holds or changes the flight.
 */
@Component({
  selector: 'app-avia-hold-refused-notice',
  templateUrl: './avia-hold-refused-notice.component.html',
  styleUrl: '../avia-price-changed-dialog/avia-price-changed-dialog.component.scss',
  imports: [TranslatePipe, PriceFormatPipe, AviaTraceIdComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AviaHoldRefusedNoticeComponent implements OnDynamicModal<AviaHoldRefusedNoticeData, void> {
  readonly data = signal<AviaHoldRefusedNoticeData | null>(null);
  readonly titleId = 'avia-hold-refused-title';

  readonly rfNumber = computed(() => this.data()?.hold.rfNumber ?? this.data()?.rfNumber ?? null);
  /** The airline's numbers are shown only when it gave both (a price change); other refusals say it generally. */
  readonly priceChanged = computed(() => {
    const hold = this.data()?.hold;
    return typeof hold?.oldPrice === 'number' && typeof hold?.newPrice === 'number';
  });

  onDynamicModalClose?: () => void;

  onDynamicModal(data: AviaHoldRefusedNoticeData): void {
    this.data.set(data);
  }

  close(): void {
    this.onDynamicModalClose?.();
  }
}
